import { Injectable } from '@angular/core';

export const BANNER_VISIBLE_CLASS: string = 'banner-visible';
export const BANNER_HEIGHT_VAR: string = '--app-banner-height';

/** Shells that can show a fixed alert banner under the toolbar. */
export type BannerOwner = 'user' | 'charity';

/**
 * Owns the `body.banner-visible` class that pushes `.banner-page` content below the fixed banner.
 *
 * Each tab shell reports its own banner state and the class is on while any shell has one. Shells
 * used to add/remove the class directly, so switching portals let the outgoing shell's teardown (or
 * a late API response) strip the class while the incoming shell's banner was still showing, and
 * the banner covered the top of the page.
 *
 * Also publishes the banner's measured height as `--app-banner-height` (see BannerHeightDirective)
 * so the offset follows Dynamic Type and wrapped text instead of a hardcoded guess.
 */
@Injectable({ providedIn: 'root' })
export class BannerLayoutService {
  private readonly visibleOwners = new Set<BannerOwner>();

  setVisible(owner: BannerOwner, visible: boolean): void {
    if (visible) {
      this.visibleOwners.add(owner);
    } else {
      this.visibleOwners.delete(owner);
    }
    document.body.classList.toggle(BANNER_VISIBLE_CLASS, this.visibleOwners.size > 0);
  }

  setHeight(px: number): void {
    if (px > 0) {
      document.documentElement.style.setProperty(BANNER_HEIGHT_VAR, `${Math.ceil(px)}px`);
    }
  }
}
